/**
 * useChat Hook
 *
 * Unified chat hook that uses SSE for real-time messages with polling fallback
 * - SSE is used when available and tab is visible
 * - Polling is used as fallback when SSE fails or tab is hidden
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useChatSSE } from "./useChatSSE";
import { useChatPolling } from "./useChatPolling";

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  body: string;
  has_masked_link: boolean;
  attachments_json?: string | any[];
  created_at: number;
}

interface UseChatOptions {
  chatId: string;
  enabled?: boolean;
  onNewMessage?: (message: Message) => void;
  preferSSE?: boolean; // Default: true
}

export function useChat({
  chatId,
  enabled = true,
  onNewMessage,
  preferSSE = true,
}: UseChatOptions) {
  const [useSSE, setUseSSE] = useState(preferSSE);
  const [connectionStatus, setConnectionStatus] = useState<"sse" | "polling" | "offline">("sse");
  const sseMessagesRef = useRef<Map<string, Message>>(new Map());

  // Handle new messages from SSE
  const [, forceUpdate] = useState(0);
  const handleSSEMessage = useCallback(
    (message: Message) => {
      sseMessagesRef.current.set(message.id, message);
      onNewMessage?.(message);
      // Force update to trigger message merge
      forceUpdate((prev) => prev + 1);
    },
    [onNewMessage]
  );

  // Handle new messages from polling
  const handlePollingMessage = useCallback(
    (message: Message) => {
      onNewMessage?.(message);
    },
    [onNewMessage]
  );

  // SSE hook
  const sse = useChatSSE({
    chatId,
    enabled: enabled && useSSE,
    onNewMessage: handleSSEMessage,
    onConnectionChange: (connected) => {
      if (connected) {
        setConnectionStatus("sse");
      } else if (useSSE) {
        // SSE failed, fallback to polling
        console.log("[useChat] SSE disconnected, falling back to polling");
        setUseSSE(false);
        setConnectionStatus("polling");
      }
    },
  });

  // Polling hook (used as fallback or when SSE is disabled)
  const polling = useChatPolling({
    chatId,
    enabled: enabled && !useSSE,
    onNewMessage: handlePollingMessage,
  });

  // Merge messages from both sources (SSE and polling)
  const [messages, setMessages] = useState<Message[]>([]);
  useEffect(() => {
    const allMessages = [
      ...polling.messages,
      ...Array.from(sseMessagesRef.current.values()),
    ];

    // Deduplicate by message ID
    const uniqueMessages = Array.from(
      new Map(allMessages.map((m) => [m.id, m])).values()
    ).sort((a, b) => a.created_at - b.created_at);

    setMessages(uniqueMessages);
  }, [polling.messages]);

  // Determine which sendMessage to use
  const sendMessage = useCallback(
    async (body: string, turnstileToken?: string) => {
      // Always use polling's sendMessage (it has the full implementation)
      return polling.sendMessage(body, turnstileToken);
    },
    [polling]
  );

  // Re-enable SSE when connection is restored
  useEffect(() => {
    if (!useSSE && preferSSE && sse.connected === false && !sse.error) {
      // Try to reconnect SSE after a delay
      const timer = setTimeout(() => {
        console.log("[useChat] Attempting to reconnect SSE");
        setUseSSE(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [useSSE, preferSSE, sse.connected, sse.error]);

  return {
    messages,
    loading: useSSE ? false : polling.loading,
    error: useSSE ? sse.error : polling.error,
    sendMessage,
    refetch: polling.refetch,
    pendingCount: polling.pendingCount,
    connectionStatus,
    sseConnected: sse.connected,
    reconnectSSE: () => {
      setUseSSE(true);
      sse.reconnect();
    },
  };
}
